import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import TenantWelcomeSplash from '../../components/portal/TenantWelcomeSplash';
import { MfaChallengePanel, MfaEnrollPanel } from '../../components/auth/MfaScreens';
import {
  Loader2,
  Mail,
  Lock,
  ArrowLeft,
  Building2,
  Crown,
  LifeBuoy,
  CheckCircle2,
} from 'lucide-react';

/**
 * Safely extract a displayable string from any axios error shape.
 * Handles: plain string, detail string, detail array (Pydantic v2), detail object.
 */
function formatAuthError(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || 'Login failed';
  const detail = data.detail;
  if (!detail) return data.message || 'Login failed';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  }
  if (typeof detail === 'object') {
    return detail.msg || JSON.stringify(detail);
  }
  return 'Login failed';
}

const firstNameOf = (fullName) => String(fullName || '').trim().split(/\s+/)[0] || '';

// Workplace roles all share the single workplace login; the label below is
// only for the welcome splash and never changes permissions.
const WORKSPACE_ROLE_LABELS = {
  workspace_owner: 'Workspace Owner',
  agency_owner: 'Workspace Owner',
  owner: 'Workspace Owner',
  super_admin: 'Workspace Owner',
  workspace_admin: 'Workspace Admin',
  admin: 'Workspace Admin',
  registered_manager: 'Registered Manager',
  branch_manager: 'Branch Manager',
  manager: 'Registered Manager',
  auditor: 'Auditor',
};

export default function LoginPage({ mode = 'workplace' }) {
  const isPlatformMode = mode === 'platform';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [welcome, setWelcome] = useState(null);
  const [mfaState, setMfaState] = useState(null); // { type: 'challenge' | 'enroll', token }
  const { login, platformLogin, loginWithGoogle, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from;
  const redirectPath = isPlatformMode
    ? '/portal/careops-admin'
    : from
    ? `${from.pathname || '/portal/dashboard'}${from.search || ''}${from.hash || ''}`
    : '/portal/dashboard';

  const proceedWithUser = (user) => {
    if (isPlatformMode && user?.role !== 'platform_owner') {
      logout();
      toast.error('This is for CareDock platform owner access only.');
      return;
    }
    if (!isPlatformMode && user?.role === 'platform_owner') {
      logout();
      toast.error('Use the separate platform-owner sign-in.');
      navigate('/careops/login', { replace: true });
      return;
    }
    toast.success('Welcome back!');
    if (!isPlatformMode) {
      setWelcome({
        firstName: firstNameOf(user?.name),
        tenantName: user?.workspace_name || user?.organisation_name || user?.workspace_display_name || '',
        roleLabel: WORKSPACE_ROLE_LABELS[user?.workspace_role]
          || WORKSPACE_ROLE_LABELS[user?.role]
          || 'Team member',
      });
      return;
    }
    navigate(redirectPath, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = isPlatformMode
        ? await platformLogin(email, password)
        : await login(email, password);
      if (result?.mfa_required) {
        setMfaState({ type: 'challenge', token: result.mfa_token });
        return;
      }
      if (result?.mfa_enrollment_required) {
        setMfaState({ type: 'enroll', token: result.mfa_token });
        return;
      }
      proceedWithUser(result);
    } catch (error) {
      toast.error(formatAuthError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (isPlatformMode) return;
    if (redirectPath.startsWith('/portal/')) {
      sessionStorage.setItem('postAuthRedirect', redirectPath);
    }
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-[#F6F8F7] flex justify-center">
      {welcome ? (
        <TenantWelcomeSplash
          greeting="Welcome"
          firstName={welcome.firstName}
          tenantName={welcome.tenantName}
          audienceLabel="Workplace Management"
          message={`Signed in as ${welcome.roleLabel}.`}
          subMessage="Opening your workspace…"
          continueLabel="Enter workspace"
          autoAdvanceMs={1200}
          onContinue={() => navigate(redirectPath, { replace: true })}
        />
      ) : (
      <>
      <div className="w-full max-w-2xl flex items-center justify-center px-5 py-12 sm:px-8">
        {mfaState ? (
          mfaState.type === 'challenge' ? (
            <MfaChallengePanel
              mfaToken={mfaState.token}
              onVerified={(user) => { setMfaState(null); proceedWithUser(user); }}
              onCancel={() => { setMfaState(null); logout(); }}
            />
          ) : (
            <MfaEnrollPanel
              mfaToken={mfaState.token}
              onEnrolled={(user) => { setMfaState(null); proceedWithUser(user); }}
              onCancel={() => { setMfaState(null); logout(); }}
            />
          )
        ) : (
        <div className="w-full max-w-[520px]">
          <div className="mb-6 flex items-center gap-2 text-primary">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white font-heading font-bold" aria-hidden="true">C</div>
            <span className="font-heading text-xl font-bold">CareDock</span>
          </div>
          <Link
            to={isPlatformMode ? '/login' : '/'}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-primary transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="h-4 w-4" />
            {isPlatformMode ? 'Workplace login' : 'CareDock website'}
          </Link>

          <div className="mb-7">
            <div className="inline-flex items-center gap-2 rounded-md border border-[#DDE7E5] bg-white px-3 py-2 text-sm font-medium text-[#0F5957] shadow-sm">
              {isPlatformMode ? <Crown className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              {isPlatformMode ? 'CareDock owner login' : 'Workplace login'}
            </div>
            <h2 className="font-heading mt-5 text-3xl sm:text-4xl font-bold text-slate-950">
              {isPlatformMode ? 'Sign in to CareDock owner admin' : 'Sign in to your CareDock workspace'}
            </h2>
            <p className="mt-3 text-base text-slate-600">
              {isPlatformMode
                ? 'Use your separate platform owner email. Tenant admin accounts are not accepted here.'
                : 'Use the email address your organisation invited. Your organisation manages your access to its workplace.'}
            </p>
          </div>

          <div className="mb-6 rounded-md border border-[#DDE7E5] bg-white p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-md bg-[#E8F4F1] text-[#0F6F6A]">
                {isPlatformMode ? <Crown className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-950">
                  {isPlatformMode ? 'Platform owner only' : 'Organisation workspace'}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  {isPlatformMode
                    ? 'Tenant admins should use the workplace login. This page only accepts platform_owner accounts.'
                    : 'Your account opens the workplace you are assigned to.'}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-800">
                {isPlatformMode ? 'Platform owner email address' : 'Work email address'}
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder={isPlatformMode ? 'owner@caredock.example' : 'you@organisation.co.uk'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 rounded-md border-[#D9E2E1] bg-white focus:border-primary focus:ring-primary/20"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-800">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 h-12 rounded-md border-[#D9E2E1] bg-white focus:border-primary focus:ring-primary/20"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 rounded-md bg-[#0F6F6A] text-white font-semibold hover:bg-[#0B5E5A]"
              data-testid="login-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                  isPlatformMode ? 'Sign in to CareDock Admin' : 'Sign in to workplace'
              )}
            </Button>
          </form>

          {!isPlatformMode && (
            <>
              <div className="relative my-7">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#DDE7E5]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#F6F8F7] text-slate-500">Or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleLogin}
                className="w-full h-12 rounded-md border-[#D9E2E1] bg-white font-medium text-slate-800 hover:bg-[#EEF5F3]"
                data-testid="google-login"
              >
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
            </>
          )}

          {isPlatformMode && (
            <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
              This is for CareDock platform owner access only.
            </div>
          )}

          <div className="mt-7 flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-center">
            <Link to="/contact" className="hover:text-primary transition-colors">
              Need a password reset?
            </Link>
            <span className="hidden sm:inline text-slate-300">|</span>
            <a href="/contact" className="inline-flex items-center justify-center gap-2 hover:text-primary transition-colors">
              <LifeBuoy className="h-4 w-4" />
              Need help signing in?
            </a>
          </div>

        </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}
